import { ArrowBack, OpenInNew, ShoppingCart } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from 'utils/tracking';
import { getCotaSimbolicaImagem, isCotaContribuicao } from 'utils/vaquinhaCota';

import { usePresentePageRules } from './PresentePage.rules';

const PresentePage: React.FC = () => {
  const { slugLista, presenteId } = useParams<{ slugLista: string; presenteId: string }>();
  const navigate = useNavigate();

  const {
    presente,
    lista,
    nomePresenteador,
    setNomePresenteador,
    mensagem,
    setMensagem,
    loading,
    processando,
    error,
    gerarPix,
    formatarValor,
  } = usePresentePageRules(slugLista, presenteId);

  const [dialogOpen, setDialogOpen] = useState(false);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !presente) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/lista/${slugLista}`)}
          sx={{ mb: 2 }}
        >
          Voltar à lista
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/lista/${slugLista}`)}
        sx={{ mb: 3 }}
      >
        Voltar à lista
      </Button>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={4}>
        {/* Informações do Presente */}
        <Card elevation={3}>
          {presente?.imagemUrl ? (
            <Box sx={{ height: 300, bgcolor: 'grey.100' }}>
              <img
                src={presente.imagemUrl}
                alt={presente.nome}
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '8px' }}
              />
            </Box>
          ) : (
            presente &&
            isCotaContribuicao(presente.categoria) && (
              <Box sx={{ height: 300 }}>
                <img
                  src={getCotaSimbolicaImagem(presente.valor)}
                  alt={presente.nome}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            )
          )}
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {presente?.nome}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              {presente?.descricao}
            </Typography>

            {presente?.produtoAfiliado?.parceiro && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Parceiro: {presente.produtoAfiliado.parceiro}
              </Typography>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" color="primary">
                {presente && formatarValor(presente.valor)}
              </Typography>
              {presente?.categoria && <Chip label={presente.categoria} />}
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<ShoppingCart />}
              onClick={() => setDialogOpen(true)}
            >
              Presentear
            </Button>

            {presente?.produtoAfiliado?.urlAfiliado && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<OpenInNew />}
                sx={{ mb: 2 }}
                onClick={() => {
                  const url = presente.produtoAfiliado?.urlAfiliado;
                  if (url) {
                    trackEvent('comprar', {
                      slugLista,
                      presenteId,
                      parceiro: presente.produtoAfiliado?.parceiro,
                    });
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                Comprar com parceiro
              </Button>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Para: {lista?.tituloLista}
            </Typography>
            <Typography color="text.secondary">
              Casamento em{' '}
              {lista?.dataCasamento && new Date(lista.dataCasamento).toLocaleDateString('pt-BR')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !processando && setDialogOpen(false)} fullWidth>
        <DialogTitle>Finalizar presente</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Preencha seus dados para gerar o PIX e presentear quem criou a lista{' '}
            {lista?.tituloLista}
          </Typography>

          <TextField
            fullWidth
            label="Seu nome completo"
            value={nomePresenteador}
            onChange={(e) => setNomePresenteador(e.target.value)}
            required
            sx={{ mb: 2 }}
            disabled={processando}
          />

          <TextField
            fullWidth
            label="Mensagem para quem vai receber (opcional)"
            multiline
            rows={3}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder={`Desejamos felicidades para ${lista?.tituloLista || 'você'}!`}
            sx={{ mb: 2 }}
            disabled={processando}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={processando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={processando ? <CircularProgress size={20} /> : <ShoppingCart />}
            disabled={processando || !nomePresenteador.trim()}
            onClick={async () => {
              trackEvent('gerar_pix', {
                slugLista,
                presenteId,
              });
              const transacaoGerada = await gerarPix();
              if (transacaoGerada) {
                navigate(`/pagamento/${transacaoGerada}`);
              }
            }}
          >
            {processando
              ? 'Gerando PIX...'
              : `Presentear - ${presente && formatarValor(presente.valor)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresentePage;
